import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, download, setCsrfToken } from "./api.js";
import Icon from "./Icon.jsx";
import BrandLogo from "./BrandLogo.jsx";
import WorkflowAnimation from "./WorkflowAnimation.jsx";
import { templateDescription, templateTitle } from "./catalog-i18n.js";
import { socialDefaults } from "./social.js";

const SessionContext = createContext(null);

function useSession() {
  return useContext(SessionContext);
}

function formatVnd(value, locale = "vi") {
  return new Intl.NumberFormat(locale === "vi" ? "vi-VN" : "en-US", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function localDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function SessionProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const value = await api("/auth/session");
      setCsrfToken(value.csrfToken);
      setSession(value);
      return value;
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return (
    <SessionContext.Provider value={{ session, ready, refresh, setSession }}>
      {children}
    </SessionContext.Provider>
  );
}

function LanguageSwitch() {
  const { i18n, t } = useTranslation();
  return (
    <button
      className="language-switch"
      aria-label={t("nav.language")}
      onClick={() => i18n.changeLanguage(i18n.language === "vi" ? "en" : "vi")}
    >
      <Icon name="language" />
      {i18n.language === "vi" ? "VI" : "EN"}
    </button>
  );
}

function Header() {
  const { t } = useTranslation();
  const { session, refresh } = useSession();
  const navigate = useNavigate();
  const logout = async () => {
    await api("/auth/logout", { method: "POST" });
    await refresh();
    navigate("/");
  };
  return (
    <header className="app-header">
      <Link className="brand" to="/">
        <BrandLogo />
        <span>
          <strong>{t("brand.name")}</strong>
          <small>{t("brand.tagline")}</small>
        </span>
      </Link>
      <nav className="nav-links">
        <Link to="/#catalog">{t("nav.explore")}</Link>
        {session?.user && <Link to="/app">{t("nav.projects")}</Link>}
        <Link to="/docs">{t("nav.docs")}</Link>
        {session?.user?.role === "admin" && (
          <Link to="/admin">{t("nav.admin")}</Link>
        )}
      </nav>
      <div className="header-actions">
        <LanguageSwitch />
        {session?.user ? (
          <button className="button ghost compact" onClick={logout}>
            {t("nav.logout")}
          </button>
        ) : (
          <Link className="button ghost compact" to="/login">
            {t("nav.login")}
          </Link>
        )}
      </div>
    </header>
  );
}

function Shell({ children, className = "" }) {
  return (
    <div className={`app-shell ${className}`}>
      <Header />
      {children}
    </div>
  );
}

function Notice({ tone = "info", children }) {
  return (
    <div className={`notice ${tone}`}>
      <span className="notice-dot" />
      {children}
    </div>
  );
}

function Home() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  return (
    <Shell>
      <main>
        <section className="hero-section">
          <div className="hero-glow" />
          <div className="hero-copy">
            <div className="eyebrow">
              <span />
              {t("home.eyebrow")}
            </div>
            <h1>{t("home.title")}</h1>
            <p>{t("home.subtitle")}</p>
            <div className="hero-actions">
              <a className="button primary" href="#catalog">
                {t("home.primary")} <Icon name="arrow-up-right" />
              </a>
              <Link className="button secondary" to="/docs">
                {t("home.secondary")}
              </Link>
            </div>
            <div className="metrics">
              <div>
                <strong>{stats?.htmlPages || 274}</strong>
                <span>{t("home.metricPages")}</span>
              </div>
              <div>
                <strong>{stats?.families || 123}</strong>
                <span>{t("home.metricFamilies")}</span>
              </div>
              <div>
                <strong>{stats?.bloggerTemplates || 1}</strong>
                <span>{t("home.metricBlogger")}</span>
              </div>
              <div>
                <strong>
                  {stats?.totalSourceBytes
                    ? `${(stats.totalSourceBytes / 1024 ** 3).toFixed(2)} GB`
                    : "1.20 GB"}
                </strong>
                <span>{t("home.metricSource")}</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="visual-window">
              <div className="window-bar">
                <i />
                <i />
                <i />
                <span>{t("home.visualUrl")}</span>
              </div>
              <div className="window-body">
                <div className="visual-sidebar">
                  <b />
                  <b />
                  <b />
                  <b />
                </div>
                <div className="visual-canvas">
                  <span className="visual-pill" />
                  <h3 />
                  <p />
                  <p />
                  <button />
                  <div className="selection-box">
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
              </div>
            </div>
            <div className="floating-card card-assets">
              <Icon name="box" />
              <span>{t("home.visualAssets")}</span>
              <strong>{t("home.visualPackage")}</strong>
            </div>
            <div className="floating-card card-check">
              <Icon name="check" />
              <span>{t("home.visualValidation")}</span>
            </div>
          </div>
        </section>
        <Catalog onStats={setStats} />
        <section className="workflow-section" id="workflow">
          <div className="section-heading">
            <span className="eyebrow">
              <span />
              {t("brand.tagline")}
            </span>
            <h2>{t("home.workflowTitle")}</h2>
          </div>
          <WorkflowAnimation />
          <div className="workflow-grid">
            {[1, 2, 3].map((number) => (
              <article key={number}>
                <em>0{number}</em>
                <h3>{t(`home.workflow${number}Title`)}</h3>
                <p>{t(`home.workflow${number}Text`)}</p>
              </article>
            ))}
          </div>
        </section>
        <ContactSection />
      </main>
    </Shell>
  );
}

function ContactSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    title: "",
    message: "",
    company: "",
  });
  const [status, setStatus] = useState("idle");
  const submit = async (event) => {
    event.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await api("/contact", { method: "POST", body: form });
      setForm({ name: "", email: "", title: "", message: "", company: "" });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };
  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  return (
    <section className="contact-section" id="contact">
      <div className="contact-copy">
        <span className="eyebrow">
          <span />
          {t("contact.eyebrow")}
        </span>
        <h2>{t("contact.title")}</h2>
        <p>{t("contact.text")}</p>
        <strong>{t("contact.social")}</strong>
        <div className="contact-socials">
          <a href={socialDefaults.facebook} target="_blank" rel="noreferrer">
            {t("contact.facebook")}
          </a>
          <a href={socialDefaults.youtube} target="_blank" rel="noreferrer">
            {t("contact.youtube")}
          </a>
          <a href={socialDefaults.zalo} target="_blank" rel="noreferrer">
            {t("contact.zalo")}
          </a>
          <a href={socialDefaults.hotline}>{t("contact.hotline")}</a>
          <a href={socialDefaults.website} target="_blank" rel="noreferrer">
            {t("contact.website")}
          </a>
        </div>
      </div>
      <form className="contact-card" onSubmit={submit}>
        <input
          className="contact-trap"
          name="company"
          value={form.company}
          onChange={update("company")}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
        />
        <div className="contact-row">
          <label>
            {t("contact.name")}
            <input
              required
              minLength={2}
              value={form.name}
              onChange={update("name")}
            />
          </label>
          <label>
            {t("contact.email")}
            <input
              required
              type="email"
              value={form.email}
              onChange={update("email")}
            />
          </label>
        </div>
        <label>
          {t("contact.subject")}
          <input
            required
            minLength={2}
            value={form.title}
            onChange={update("title")}
          />
        </label>
        <label>
          {t("contact.message")}
          <textarea
            required
            minLength={10}
            rows={5}
            value={form.message}
            onChange={update("message")}
          />
        </label>
        <div className="contact-submit-row">
          <small>{t("contact.privacy")}</small>
          <button
            className="button primary"
            type="submit"
            disabled={status === "sending"}
          >
            {t(status === "sending" ? "contact.sending" : "contact.submit")}
          </button>
        </div>
        {status === "success" && <Notice>{t("contact.success")}</Notice>}
        {status === "error" && (
          <Notice tone="danger">{t("contact.error")}</Notice>
        )}
      </form>
    </section>
  );
}

function Catalog({ onStats }) {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState({ pages: [], families: [], stats: null });
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api("/pricing").then((value) => setPricing(value.pricing));
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api(`/catalog?kind=${kind}`)
        .then((value) => {
          if (!active) return;
          setData(value);
          onStats?.(value.stats);
        })
        .catch((issue) => active && setError(issue.message))
        .finally(() => active && setLoading(false));
    }, 160);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [kind, onStats]);
  const customize = async (page) => {
    setError("");
    if (!session?.user)
      return navigate("/login", { state: { demoId: page.id } });
    setBusy(page.id);
    try {
      const value = await api("/projects", {
        method: "POST",
        body: {
          demoId: page.id,
          name: templateTitle(page, i18n.language, t),
        },
      });
      navigate(`/editor/${value.project.id}`);
    } catch (issue) {
      setError(issue.message);
    } finally {
      setBusy(null);
    }
  };
  const normalizedQuery = query.trim().toLocaleLowerCase(i18n.language);
  const pages = data.pages
    .filter((page) => {
      if (!normalizedQuery) return true;
      return `${templateTitle(page, i18n.language, t)} ${templateDescription(page, i18n.language, t)} ${page.familyId}`
        .toLocaleLowerCase(i18n.language)
        .includes(normalizedQuery);
    })
    .slice(0, 36);
  return (
    <section className="catalog-section" id="catalog">
      <div className="section-heading row">
        <div>
          <span className="eyebrow">
            <span />
            {t("catalog.eyebrow")}
          </span>
          <h2>{t("catalog.title")}</h2>
          <p>{t("catalog.subtitle")}</p>
        </div>
        <div className="catalog-controls">
          <label className="search-box">
            <Icon name="search" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("catalog.searchPlaceholder")}
            />
          </label>
          <div className="segmented">
            {["all", "landing", "website", "blogger"].map((item) => (
              <button
                key={item}
                className={kind === item ? "active" : ""}
                onClick={() => setKind(item)}
              >
                {t(`catalog.filter${item[0].toUpperCase()}${item.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {pricing && (
        <div className="pricing-banner">
          <div className="pricing-orb">
            <Icon name="sparkles" size={22} />
          </div>
          <div>
            <span>{t("commerce.eyebrow")}</span>
            <strong>{t("commerce.bannerTitle")}</strong>
            <p>{t("commerce.bannerText")}</p>
          </div>
          <div className="pricing-banner-price">
            {pricing.promoActive && (
              <del>{formatVnd(pricing.basePriceVnd, i18n.language)}</del>
            )}
            <strong>{formatVnd(pricing.currentPriceVnd, i18n.language)}</strong>
            {pricing.promoActive && <span>{t("commerce.promoActive")}</span>}
          </div>
        </div>
      )}
      {error && (
        <Notice tone="danger">
          {t(`errors.${error}`, { defaultValue: t("common.error") })}
        </Notice>
      )}
      <div className="template-grid">
        {loading &&
          Array.from({ length: 6 }, (_, index) => (
            <article className="template-card skeleton-card" key={index}>
              <div className="template-preview skeleton-block" />
              <div className="template-info">
                <span className="skeleton-line short" />
                <span className="skeleton-line title" />
                <span className="skeleton-line" />
                <span className="skeleton-line" />
              </div>
            </article>
          ))}
        {!loading &&
          pages.map((page) => {
            const localizedTitle = templateTitle(page, i18n.language, t);
            const localizedDescription = templateDescription(
              page,
              i18n.language,
              t,
            );
            return (
              <article className="template-card" key={page.id}>
                <button
                  className="template-preview"
                  onClick={() => setPreview(page)}
                  aria-label={`${t("catalog.preview")} ${localizedTitle}`}
                >
                  {page.thumbnail ? (
                    <img src={page.thumbnail} alt="" loading="lazy" />
                  ) : (
                    <div className="image-fallback">
                      <span>{localizedTitle.slice(0, 1)}</span>
                    </div>
                  )}
                  <span className="preview-overlay">
                    <Icon name="arrow-up-right" />
                    {t("catalog.preview")}
                  </span>
                </button>
                <div className="template-info">
                  <div className="template-tags">
                    <span>
                      {page.kind === "blogger"
                        ? t("catalog.filterBlogger")
                        : page.kind === "landing"
                          ? t("catalog.filterLanding")
                          : t("catalog.filterWebsite")}
                    </span>
                    <span>
                      {t("catalog.pageCount", { count: page.sitePages.length })}
                    </span>
                  </div>
                  <h3>{localizedTitle}</h3>
                  <p>{localizedDescription}</p>
                  <div className="template-meta">
                    <span>
                      <Icon name="layers" />
                      {t("catalog.sectionCount", { count: page.sectionCount })}
                    </span>
                    <button
                      className="text-button"
                      disabled={busy === page.id}
                      onClick={() => customize(page)}
                    >
                      {busy === page.id
                        ? t("common.loading")
                        : t("catalog.customize")}{" "}
                      <Icon name="arrow-right" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
      </div>
      {!loading && !pages.length && (
        <div className="empty-state">{t("catalog.empty")}</div>
      )}
      {preview && (
        <PreviewModal
          page={preview}
          onClose={() => setPreview(null)}
          onCustomize={() => customize(preview)}
        />
      )}
    </section>
  );
}

function PreviewModal({ page, onClose, onCustomize }) {
  const { t, i18n } = useTranslation();
  const [device, setDevice] = useState("desktop");
  const localizedTitle = templateTitle(page, i18n.language, t);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="preview-modal">
        <div className="modal-toolbar">
          <div>
            <strong>{localizedTitle}</strong>
            <span>
              {t("catalog.pageCount", { count: page.sitePages.length })}
            </span>
          </div>
          <div className="device-switch">
            {["desktop", "tablet", "mobile"].map((item) => (
              <button
                className={device === item ? "active" : ""}
                key={item}
                onClick={() => setDevice(item)}
                aria-label={t(`editor.${item}`)}
              >
                <Icon name={item} />
              </button>
            ))}
          </div>
          <button className="button primary compact" onClick={onCustomize}>
            {t("catalog.customize")}
          </button>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className={`iframe-stage ${device}`}>
          <iframe
            title={localizedTitle}
            sandbox="allow-scripts allow-forms allow-popups"
            src={`/api/demos/${page.id}/preview`}
          />
        </div>
      </div>
    </div>
  );
}

function Login() {
  const { t } = useTranslation();
  const { session, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [register, setRegister] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  if (session?.user) return <Navigate to="/app" replace />;
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await api(register ? "/auth/register" : "/auth/login", {
        method: "POST",
        body: form,
      });
      await refresh();
      navigate(
        location.state?.demoId
          ? `/?demo=${location.state.demoId}#catalog`
          : "/app",
      );
    } catch {
      setError("invalid");
    }
  };
  return (
    <Shell className="auth-shell">
      <main className="auth-page">
        <div className="auth-art">
          <div className="auth-orbit">
            <span className="orbit-dot" />
            <span className="orbit-dot" />
            <BrandLogo compact />
          </div>
          <h2>{t("home.title")}</h2>
          <p>{t("home.subtitle")}</p>
        </div>
        <form className="auth-card" onSubmit={submit}>
          <span className="eyebrow">
            <span />
            {t("brand.tagline")}
          </span>
          <h1>{register ? t("auth.register") : t("auth.title")}</h1>
          <p>{t("auth.subtitle")}</p>
          {error && <Notice tone="danger">{t(`auth.${error}`)}</Notice>}
          {register && (
            <label>
              {t("auth.name")}
              <input
                required
                minLength={2}
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
              />
            </label>
          )}
          <label>
            {t("auth.email")}
            <input
              type="email"
              required
              value={form.email}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
            />
          </label>
          <label>
            {t("auth.password")}
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
            />
          </label>
          <button className="button primary wide" type="submit">
            {register ? t("auth.register") : t("auth.submit")}
          </button>
          {session?.googleEnabled && (
            <>
              <div className="divider">
                <span>{t("auth.or")}</span>
              </div>
              <a className="button secondary wide" href="/api/auth/google">
                <Icon name="google" />
                {t("auth.google")}
              </a>
            </>
          )}
          <button
            className="auth-switch"
            type="button"
            onClick={() => setRegister(!register)}
          >
            {register ? t("auth.existing") : t("auth.new")}{" "}
            <strong>{register ? t("auth.submit") : t("auth.register")}</strong>
          </button>
        </form>
      </main>
    </Shell>
  );
}

function RequireAuth({ children, admin = false }) {
  const { session, ready } = useSession();
  if (!ready) return <Loading />;
  if (!session?.user) return <Navigate to="/login" replace />;
  if (admin && session.user.role !== "admin")
    return <Navigate to="/app" replace />;
  return children;
}

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="loading-screen">
      <div className="loader" />
      <span>{t("common.loading")}</span>
    </div>
  );
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api("/projects").then((value) => setProjects(value.projects));
  }, []);
  return (
    <Shell>
      <main className="dashboard-page">
        <div className="dashboard-hero">
          <div>
            <span className="eyebrow">
              <span />
              {t(`plans.${session.user.planId}`)}
            </span>
            <h1>
              {t("dashboard.greeting", {
                name: session.user.name.split(" ")[0],
              })}
            </h1>
            <p>{t("dashboard.subtitle")}</p>
          </div>
          {session.user.role === "admin" ? (
            <div className="quota-card">
              <div
                className="quota-ring"
                style={{
                  "--quota": "100%",
                }}
              >
                <strong>∞</strong>
              </div>
              <span>{t("dashboard.adminExport")}</span>
            </div>
          ) : null}
        </div>
        <div className="section-heading row">
          <div>
            <h2>{t("dashboard.projectsTitle")}</h2>
          </div>
          <Link className="button primary compact" to="/#catalog">
            <Icon name="plus" /> {t("dashboard.newProject")}
          </Link>
        </div>
        {projects === null ? (
          <Loading />
        ) : projects.length ? (
          <div className="project-grid">
            {projects.map((project) => (
              <Link
                className="project-card"
                to={`/editor/${project.id}`}
                key={project.id}
              >
                <div className="project-icon">{project.name.slice(0, 1)}</div>
                <div>
                  <h3>{project.name}</h3>
                  <p>{project.demo_id}</p>
                  <span>
                    {t("dashboard.updated", {
                      date: new Intl.DateTimeFormat(i18n.language).format(
                        new Date(project.updated_at),
                      ),
                    })}
                  </span>
                </div>
                <Icon name="arrow-right" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">{t("dashboard.empty")}</div>
        )}
      </main>
    </Shell>
  );
}

function Editor() {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [family, setFamily] = useState(null);
  const [pageId, setPageId] = useState("");
  const [model, setModel] = useState(null);
  const [content, setContent] = useState({ pages: {}, theme: {} });
  const [lease, setLease] = useState(null);
  const [status, setStatus] = useState("saved");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("content");
  const [selected, setSelected] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [query, setQuery] = useState("");
  const [device, setDevice] = useState("desktop");
  const [exportMode, setExportMode] = useState("minimal");
  const [exportReport, setExportReport] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const contentRef = useRef(content);
  const projectRef = useRef(project);
  const leaseRef = useRef(lease);
  const previewRef = useRef(null);
  useEffect(() => {
    contentRef.current = content;
  }, [content]);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);
  useEffect(() => {
    leaseRef.current = lease;
  }, [lease]);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const value = await api(`/projects/${projectId}`);
        if (disposed) return;
        setProject({ ...value.project, content: undefined });
        setFamily(value.family);
        setContent(value.project.content || { pages: {}, theme: {} });
        setPageId(value.project.demo_id);
        const acquired = await api(`/projects/${projectId}/lease`, {
          method: "POST",
        });
        if (!disposed) setLease(acquired);
      } catch (issue) {
        if (!disposed) setError(issue.message);
      }
    })();
    return () => {
      disposed = true;
      if (leaseRef.current?.token)
        api(`/projects/${projectId}/lease`, {
          method: "DELETE",
          headers: { "x-edit-lease": leaseRef.current.token },
        }).catch(() => {});
    };
  }, [projectId]);
  const loadPurchase = useCallback(() => {
    api(`/projects/${projectId}/purchase`)
      .then(setPurchase)
      .catch((issue) => setError(issue.message));
  }, [projectId]);
  useEffect(() => {
    if (project) loadPurchase();
  }, [project?.revision, loadPurchase]);
  useEffect(() => {
    const pending = purchase?.orders.some(
      (order) =>
        order.revision === purchase.currentRevision &&
        order.status === "pending",
    );
    if (!pending) return undefined;
    const timer = setInterval(loadPurchase, 12000);
    return () => clearInterval(timer);
  }, [purchase, loadPurchase]);
  useEffect(() => {
    if (pageId && lease)
      api(`/projects/${projectId}/pages/${pageId}/model`)
        .then(setModel)
        .catch((issue) => setError(issue.message));
  }, [pageId, lease, projectId]);
  useEffect(() => {
    setSelected("");
    setSelectedSection("");
  }, [pageId]);
  useEffect(() => {
    if (!lease?.token) return undefined;
    const timer = setInterval(async () => {
      try {
        setLease(
          await api(`/projects/${projectId}/lease`, {
            method: "PUT",
            headers: { "x-edit-lease": lease.token },
          }),
        );
      } catch (issue) {
        setError(issue.message);
      }
    }, 25000);
    return () => clearInterval(timer);
  }, [lease?.token, projectId]);
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.source !== "canvas-package-studio") return;
      if (event.data.type === "select") {
        setSelected(event.data.fieldId);
        const selectedField = model?.fields.find(
          (field) => field.id === event.data.fieldId,
        );
        if (selectedField?.sectionId)
          setSelectedSection(selectedField.sectionId);
        setTab("content");
        setTimeout(
          () =>
            document
              .getElementById(`field-${event.data.fieldId}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" }),
          50,
        );
      }
      if (
        event.data.type === "navigate" &&
        family?.pages.includes(event.data.pageId)
      )
        setPageId(event.data.pageId);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [family, model]);

  const save = useCallback(async () => {
    if (!leaseRef.current?.token || status === "saving") return;
    if (status === "saved") return projectRef.current;
    setStatus("saving");
    setError("");
    try {
      const value = await api(`/projects/${projectId}`, {
        method: "PUT",
        headers: { "x-edit-lease": leaseRef.current.token },
        body: {
          name: projectRef.current.name,
          revision: projectRef.current.revision,
          content: contentRef.current,
        },
      });
      setProject((current) => ({
        ...current,
        revision: value.project.revision,
        name: value.project.name,
      }));
      projectRef.current = {
        ...projectRef.current,
        revision: value.project.revision,
        name: value.project.name,
      };
      setStatus("saved");
      return value.project;
    } catch (issue) {
      setError(issue.message);
      setStatus("error");
    }
  }, [projectId, status]);
  useEffect(() => {
    if (status !== "dirty") return undefined;
    const timer = setTimeout(save, 700);
    return () => clearTimeout(timer);
  }, [status, content, save]);

  const mutateContent = (producer) => {
    setContent((current) => {
      const next = structuredClone(current);
      producer(next);
      return next;
    });
    setStatus("dirty");
  };
  const currentPage = content.pages?.[pageId] || {
    fields: {},
    hiddenSections: [],
    sectionOrder: [],
  };
  const postPreview = (payload) =>
    previewRef.current?.contentWindow?.postMessage(
      { source: "easyweb-editor", ...payload },
      "*",
    );
  const syncPreview = () =>
    postPreview({
      type: "sync",
      fields: currentPage.fields || {},
      theme: content.theme || {},
      sections: (model?.sections || []).map((section) => ({
        id: section.id,
        hidden: currentPage.hiddenSections?.includes(section.id),
      })),
      order: currentPage.sectionOrder || [],
    });
  const setField = (id, value) => {
    mutateContent((next) => {
      next.pages ||= {};
      next.pages[pageId] ||= { fields: {}, hiddenSections: [] };
      next.pages[pageId].fields ||= {};
      next.pages[pageId].fields[id] = value;
    });
    postPreview({ type: "field", id, value });
  };
  const toggleSection = (id) => {
    const hidden = !currentPage.hiddenSections?.includes(id);
    mutateContent((next) => {
      next.pages ||= {};
      next.pages[pageId] ||= { fields: {}, hiddenSections: [] };
      const items = (next.pages[pageId].hiddenSections ||= []);
      const index = items.indexOf(id);
      if (index >= 0) items.splice(index, 1);
      else items.push(id);
    });
    postPreview({ type: "section", id, hidden });
  };
  const moveSection = (id, direction) => {
    const order = currentPage.sectionOrder?.length
      ? [...currentPage.sectionOrder]
      : model?.sections.map((section) => section.id) || [];
    const index = order.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    mutateContent((next) => {
      next.pages ||= {};
      next.pages[pageId] ||= {
        fields: {},
        hiddenSections: [],
        sectionOrder: [],
      };
      next.pages[pageId].sectionOrder = order;
    });
    postPreview({ type: "order", value: order });
  };
  const doExport = async (orderId = null) => {
    setStatus("exporting");
    setExportReport(null);
    try {
      await save();
      await download(`/projects/${projectId}/export`, {
        mode: exportMode,
        locale: i18n.language,
        orderId,
      });
      setStatus("saved");
    } catch (issue) {
      setExportReport(issue.payload?.report || null);
      setError(issue.message);
      setStatus("error");
    }
  };
  const requestPayment = async () => {
    setPurchaseBusy(true);
    setError("");
    try {
      await save();
      await api(`/projects/${projectId}/payment-orders`, { method: "POST" });
      await loadPurchase();
    } catch (issue) {
      setError(issue.message);
    } finally {
      setPurchaseBusy(false);
    }
  };
  const invite = async () => {
    try {
      await api(`/projects/${projectId}/members`, {
        method: "POST",
        body: { email: inviteEmail },
      });
      setInviteEmail("");
    } catch (issue) {
      setError(issue.message);
    }
  };
  if (!project && !error) return <Loading />;
  if (!project)
    return (
      <Shell>
        <main className="dashboard-page">
          <Notice tone="danger">
            {t(`errors.${error}`, { defaultValue: t("common.error") })}
          </Notice>
        </main>
      </Shell>
    );
  const visibleFields =
    model?.fields.filter(
      (field) =>
        (!selectedSection ||
          field.sectionId === selectedSection ||
          field.type === "meta") &&
        (!query ||
          `${field.label} ${field.value}`
            .toLowerCase()
            .includes(query.toLowerCase())),
    ) || [];
  return (
    <div className="editor-shell">
      <header className="editor-header">
        <button
          className="icon-button"
          onClick={() => navigate("/app")}
          aria-label={t("editor.leave")}
        >
          <Icon name="arrow-left" />
        </button>
        <Link className="mini-brand" to="/">
          <BrandLogo compact />
        </Link>
        <input
          className="project-name"
          value={project.name}
          onChange={(event) => {
            setProject({ ...project, name: event.target.value });
            projectRef.current = { ...project, name: event.target.value };
            setStatus("dirty");
          }}
          aria-label={t("editor.projectName")}
        />
        <div className="save-status">
          <span className={status} />
          {status === "saving"
            ? t("common.saving")
            : status === "exporting"
              ? t("editor.building")
              : t("common.saved")}
        </div>
        <div className="device-switch">
          {["desktop", "tablet", "mobile"].map((item) => (
            <button
              key={item}
              className={device === item ? "active" : ""}
              onClick={() => setDevice(item)}
              aria-label={t(`editor.${item}`)}
            >
              <Icon name={item} />
            </button>
          ))}
        </div>
        <LanguageSwitch />
        <button
          className="button primary compact"
          onClick={() => setTab("export")}
        >
          {t("editor.export")}
        </button>
      </header>
      <div className="editor-layout">
        <aside className="editor-outline">
          <div className="outline-heading">
            <Icon name="layers" />
            <span>
              <small>{t("editor.structure")}</small>
              <strong>{t("editor.pageOutline")}</strong>
            </span>
          </div>
          <div className="outline-scroll">
            <div className="outline-group">
              <small>{t("editor.sitePages")}</small>
              {family?.pages.map((id) => (
                <button
                  className={`outline-page ${pageId === id ? "active" : ""}`}
                  onClick={() => {
                    setPageId(id);
                    setSelectedSection("");
                  }}
                  key={id}
                >
                  <Icon name="pages" />
                  <span>{id.replace(/^demo-/, "").replace(/-/g, " ")}</span>
                </button>
              ))}
            </div>
            <div className="outline-group sections-outline">
              <small>{t("common.sections")}</small>
              <button
                className={`outline-page ${selectedSection ? "" : "active"}`}
                onClick={() => setSelectedSection("")}
              >
                <Icon name="layers" />
                <span>{t("editor.allSections")}</span>
                <i>{model?.fields.length || 0}</i>
              </button>
              {model?.sections.map((section) => {
                const hidden = currentPage.hiddenSections?.includes(section.id);
                const count = model.fields.filter(
                  (field) => field.sectionId === section.id,
                ).length;
                return (
                  <div
                    className={`outline-section ${selectedSection === section.id ? "active" : ""}`}
                    key={section.id}
                  >
                    <button
                      className="outline-section-main"
                      onClick={() => {
                        setSelectedSection(section.id);
                        setTab("content");
                        postPreview({
                          type: "focus-section",
                          id: section.id,
                        });
                      }}
                    >
                      <span>{section.label}</span>
                      <small>{t("editor.fieldCount", { count })}</small>
                    </button>
                    <div className="outline-section-actions">
                      <button
                        className={hidden ? "" : "visible"}
                        onClick={() => toggleSection(section.id)}
                        aria-label={
                          hidden ? t("editor.hidden") : t("editor.visible")
                        }
                      >
                        <Icon name={hidden ? "eye-off" : "eye"} />
                      </button>
                      <button
                        onClick={() => moveSection(section.id, -1)}
                        aria-label={t("editor.moveUp")}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveSection(section.id, 1)}
                        aria-label={t("editor.moveDown")}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
        <aside className="editor-panel">
          <div className="editor-tabs">
            {["content", "theme", "sitePages", "export"].map((item) => (
              <button
                key={item}
                className={tab === item ? "active" : ""}
                onClick={() => setTab(item)}
              >
                <Icon
                  name={
                    item === "content"
                      ? "edit"
                      : item === "theme"
                        ? "palette"
                        : item === "sitePages"
                          ? "pages"
                          : "download"
                  }
                />
                {t(`editor.${item}`)}
              </button>
            ))}
          </div>
          <div className="panel-scroll">
            {error && (
              <Notice tone="danger">
                {t(`errors.${error}`, { defaultValue: error })}
              </Notice>
            )}
            {tab === "content" && (
              <>
                <label className="search-box panel-search">
                  <Icon name="search" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("editor.searchFields")}
                  />
                </label>
                {!model ? (
                  <Loading />
                ) : (
                  <div className="field-list">
                    {visibleFields.map((field) => (
                      <FieldEditor
                        key={field.id}
                        field={field}
                        value={currentPage.fields?.[field.id]}
                        selected={selected === field.id}
                        onChange={(value) => setField(field.id, value)}
                        onUpload={async (file) => {
                          const form = new FormData();
                          form.append("file", file);
                          const value = await api(
                            `/projects/${projectId}/uploads`,
                            { method: "POST", body: form },
                          );
                          setField(field.id, {
                            src: value.url,
                            alt: field.alt || "",
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
                {model && (
                  <div className="panel-section">
                    <h3>{t("editor.sectionVisibility")}</h3>
                    {model?.sections.map((section) => {
                      const hidden = currentPage.hiddenSections?.includes(
                        section.id,
                      );
                      return (
                        <div className="section-toggle" key={section.id}>
                          <button onClick={() => toggleSection(section.id)}>
                            <span>{section.label}</span>
                            <i className={hidden ? "" : "on"}>
                              {hidden
                                ? t("editor.hidden")
                                : t("editor.visible")}
                            </i>
                          </button>
                          <span className="section-order">
                            <button
                              onClick={() => moveSection(section.id, -1)}
                              aria-label={t("editor.moveUp")}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveSection(section.id, 1)}
                              aria-label={t("editor.moveDown")}
                            >
                              ↓
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {tab === "theme" && (
              <ThemeEditor
                theme={content.theme || {}}
                onChange={(key, value) => {
                  mutateContent((next) => {
                    next.theme ||= {};
                    next.theme[key] = value;
                  });
                  postPreview({
                    type: "theme",
                    value: { ...(content.theme || {}), [key]: value },
                  });
                }}
              />
            )}
            {tab === "sitePages" && (
              <div className="panel-section">
                <h3>{t("editor.sitePages")}</h3>
                {family?.pages.map((id) => (
                  <button
                    className={`page-row ${pageId === id ? "active" : ""}`}
                    onClick={() => setPageId(id)}
                    key={id}
                  >
                    <span>{id.replace(/^demo-/, "").replace(/-/g, " ")}</span>
                    <Icon name="arrow-right" />
                  </button>
                ))}
                <div className="invite-box">
                  <h3>{t("editor.invite")}</h3>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                    placeholder={t("editor.inviteEmail")}
                  />
                  <button className="button secondary wide" onClick={invite}>
                    {t("editor.inviteAction")}
                  </button>
                </div>
              </div>
            )}
            {tab === "export" && (
              <ExportPanel
                sourceType={project.source_type || "html"}
                mode={exportMode}
                setMode={setExportMode}
                status={status}
                report={exportReport}
                onExport={doExport}
                purchase={purchase}
                purchaseBusy={purchaseBusy}
                onRequestPayment={requestPayment}
                locale={i18n.language}
                isAdmin={session.user.role === "admin"}
              />
            )}
          </div>
        </aside>
        <main className={`editor-preview ${device}`}>
          <div className="preview-label">
            <span className="live-dot" />
            {t("editor.preview")}
            <small>{pageId}</small>
          </div>
          <div className="preview-frame">
            <iframe
              ref={previewRef}
              key={pageId}
              title={pageId}
              sandbox="allow-scripts allow-forms allow-popups"
              src={`/api/projects/${projectId}/pages/${pageId}/preview`}
              onLoad={syncPreview}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

function FieldEditor({ field, value, selected, onChange, onUpload }) {
  const { t } = useTranslation();
  const display = value ?? field.value;
  if (field.type === "link") {
    const current =
      typeof display === "object"
        ? display
        : { text: display, href: field.href };
    return (
      <div
        id={`field-${field.id}`}
        className={`field-card ${selected ? "selected" : ""}`}
      >
        <small>{selected ? t("editor.selected") : field.tag}</small>
        <label>
          {t("editor.linkText")}
          <input
            value={current.text || ""}
            onChange={(event) =>
              onChange({ ...current, text: event.target.value })
            }
          />
        </label>
        <label>
          {t("editor.linkUrl")}
          <input
            value={current.href || ""}
            onChange={(event) =>
              onChange({ ...current, href: event.target.value })
            }
          />
        </label>
      </div>
    );
  }
  if (field.type === "image") {
    const current =
      typeof display === "object" ? display : { src: display, alt: field.alt };
    return (
      <div
        id={`field-${field.id}`}
        className={`field-card ${selected ? "selected" : ""}`}
      >
        <small>{selected ? t("editor.selected") : field.tag}</small>
        <label>
          {t("editor.imageUrl")}
          <input
            value={current.src || ""}
            onChange={(event) =>
              onChange({ ...current, src: event.target.value })
            }
          />
        </label>
        <label>
          {t("editor.altText")}
          <input
            value={current.alt || ""}
            onChange={(event) =>
              onChange({ ...current, alt: event.target.value })
            }
          />
        </label>
        <label className="upload-button">
          ↑ {t("editor.upload")}
          <input
            hidden
            type="file"
            accept="image/*,video/*"
            onChange={(event) =>
              event.target.files?.[0] && onUpload(event.target.files[0])
            }
          />
        </label>
      </div>
    );
  }
  if (field.type === "form") {
    const current =
      typeof display === "object"
        ? display
        : { endpoint: display, method: field.method || "POST" };
    return (
      <div
        id={`field-${field.id}`}
        className={`field-card ${selected ? "selected" : ""}`}
      >
        <small>{selected ? t("editor.selected") : field.tag}</small>
        <label>
          {t("editor.formEndpoint")}
          <input
            value={current.endpoint || ""}
            onChange={(event) =>
              onChange({ ...current, endpoint: event.target.value })
            }
          />
        </label>
        <label>
          {t("editor.formMethod")}
          <select
            value={current.method || "POST"}
            onChange={(event) =>
              onChange({ ...current, method: event.target.value })
            }
          >
            <option>POST</option>
            <option>GET</option>
          </select>
        </label>
      </div>
    );
  }
  return (
    <div
      id={`field-${field.id}`}
      className={`field-card ${selected ? "selected" : ""}`}
    >
      <small>
        {selected
          ? t("editor.selected")
          : field.id.startsWith("seo-")
            ? "SEO"
            : field.tag}
      </small>
      <label>
        {field.id === "seo-title"
          ? t("editor.seoTitle")
          : field.id === "seo-description"
            ? t("editor.seoDescription")
            : t("editor.text")}
        <textarea
          rows={Math.min(
            5,
            Math.max(2, Math.ceil(String(display || "").length / 55)),
          )}
          value={display || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}

function ThemeEditor({ theme, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="panel-section theme-editor">
      <h3>{t("editor.theme")}</h3>
      <label>
        {t("editor.primaryColor")}
        <span className="color-input">
          <input
            type="color"
            value={theme.primary || "#7c5cff"}
            onChange={(event) => onChange("primary", event.target.value)}
          />
          <input
            value={theme.primary || "#7c5cff"}
            onChange={(event) => onChange("primary", event.target.value)}
          />
        </span>
      </label>
      <label>
        {t("editor.backgroundColor")}
        <span className="color-input">
          <input
            type="color"
            value={theme.background || "#ffffff"}
            onChange={(event) => onChange("background", event.target.value)}
          />
          <input
            value={theme.background || "#ffffff"}
            onChange={(event) => onChange("background", event.target.value)}
          />
        </span>
      </label>
      <label>
        {t("editor.font")}
        <select
          value={theme.font || "Inter"}
          onChange={(event) => onChange("font", event.target.value)}
        >
          {[
            "Inter",
            "Poppins",
            "Montserrat",
            "Lato",
            "Roboto",
            "Open Sans",
            "system-ui",
            "serif",
          ].map((font) => (
            <option key={font}>{font}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function ExportPanel({
  sourceType,
  mode,
  setMode,
  status,
  report,
  onExport,
  purchase,
  purchaseBusy,
  onRequestPayment,
  locale,
  isAdmin,
}) {
  const { t } = useTranslation();
  const blogger = sourceType === "blogger";
  const currentOrder = purchase?.orders.find(
    (order) => order.revision === purchase.currentRevision,
  );
  const paidOrders = purchase?.orders.filter(
    (order) => order.status === "approved",
  );
  return (
    <div className="panel-section export-panel">
      <h2>{t(blogger ? "editor.bloggerExportTitle" : "editor.exportTitle")}</h2>
      {!blogger && (
        <>
          <button
            className={`export-mode ${mode === "minimal" ? "active" : ""}`}
            onClick={() => setMode("minimal")}
          >
            <Icon name="box" size={20} />
            <div>
              <strong>{t("editor.minimalTitle")}</strong>
              <p>{t("editor.minimalText")}</p>
            </div>
            <i />
          </button>
          <button
            className={`export-mode ${mode === "safe" ? "active" : ""}`}
            onClick={() => setMode("safe")}
          >
            <Icon name="shield" size={20} />
            <div>
              <strong>{t("editor.safeTitle")}</strong>
              <p>{t("editor.safeText")}</p>
            </div>
            <i />
          </button>
          <Notice>{t("editor.sanitized")}</Notice>
        </>
      )}
      {blogger && <Notice>{t("editor.bloggerExportText")}</Notice>}
      {isAdmin ? (
        <div className="admin-export-pass">
          <Icon name="shield" size={21} />
          <div>
            <strong>{t("commerce.adminPassTitle")}</strong>
            <p>{t("commerce.adminPassText")}</p>
          </div>
        </div>
      ) : purchase ? (
        <div className="checkout-card">
          <div className="checkout-head">
            <div>
              <span>
                {t("commerce.productRevision", {
                  revision: purchase.currentRevision,
                })}
              </span>
              <h3>{t("commerce.checkoutTitle")}</h3>
            </div>
            <div className="checkout-price">
              {purchase.pricing.promoActive && (
                <del>{formatVnd(purchase.pricing.basePriceVnd, locale)}</del>
              )}
              <strong>
                {formatVnd(purchase.pricing.currentPriceVnd, locale)}
              </strong>
            </div>
          </div>
          {currentOrder?.status === "approved" ? (
            <div className="payment-status approved">
              <Icon name="check" size={18} />
              <div>
                <strong>{t("commerce.approvedTitle")}</strong>
                <p>{t("commerce.unlimitedText")}</p>
              </div>
            </div>
          ) : currentOrder?.status === "pending" ? (
            <>
              <div className="bank-card">
                <span>{t("commerce.bankTransfer")}</span>
                <strong>{purchase.pricing.bank.accountNumber}</strong>
                <p>{purchase.pricing.bank.accountName}</p>
                <p>{purchase.pricing.bank.name}</p>
                <p>{purchase.pricing.bank.branch}</p>
                <div>
                  <small>{t("commerce.transferContent")}</small>
                  <code>{currentOrder.transferReference}</code>
                </div>
              </div>
              <div className="payment-status pending">
                <span />
                <div>
                  <strong>{t("commerce.pendingTitle")}</strong>
                  <p>{t("commerce.pendingText")}</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="checkout-copy">{t("commerce.checkoutText")}</p>
              <button
                className="button primary wide"
                disabled={purchaseBusy}
                onClick={onRequestPayment}
              >
                {purchaseBusy
                  ? t("commerce.creatingOrder")
                  : t("commerce.requestPayment")}
              </button>
            </>
          )}
          {purchase.pricing.promoActive && (
            <p className="promo-until">
              {t("commerce.promoUntil", {
                date: new Intl.DateTimeFormat(locale, {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(purchase.pricing.promoEndsAt)),
              })}
            </p>
          )}
        </div>
      ) : null}
      {report && (
        <Notice tone="danger">
          <div>
            <strong>{t("editor.brokenTitle")}</strong>
            <p>{t("editor.brokenText")}</p>
            <ul>
              {report.brokenLinks?.slice(0, 8).map((item, index) => (
                <li key={index}>
                  {item.from} <Icon name="arrow-right" /> {item.reference}
                </li>
              ))}
            </ul>
          </div>
        </Notice>
      )}
      {(isAdmin || currentOrder?.status === "approved") && (
        <button
          className="button primary wide"
          disabled={status === "exporting"}
          onClick={() => onExport(currentOrder?.id || null)}
        >
          {status === "exporting"
            ? t("editor.building")
            : t(blogger ? "editor.downloadBlogger" : "editor.download")}
        </button>
      )}
      {!isAdmin &&
        paidOrders?.some(
          (order) => order.revision !== purchase.currentRevision,
        ) && (
          <div className="paid-history">
            <h3>{t("commerce.paidVersions")}</h3>
            {paidOrders
              .filter((order) => order.revision !== purchase.currentRevision)
              .map((order) => (
                <button key={order.id} onClick={() => onExport(order.id)}>
                  <span>
                    {t("commerce.revision", { revision: order.revision })}
                    <small>{order.transferReference}</small>
                  </span>
                  <strong>{t("commerce.downloadAgain")}</strong>
                </button>
              ))}
          </div>
        )}
    </div>
  );
}

function Docs() {
  const { t, i18n } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [pageId, setPageId] = useState("");
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api("/catalog").then((value) => {
      setCatalog(value.pages);
      setPageId(value.pages[0]?.id || "");
    });
  }, []);
  useEffect(() => {
    if (pageId) api(`/docs/demos/${pageId}`).then(setDetail);
  }, [pageId]);
  const general = [
    "overview",
    "workflow",
    "editing",
    "forms",
    "export",
    "deployment",
    "security",
  ];
  return (
    <Shell>
      <main className="docs-page">
        <div className="docs-hero">
          <span className="eyebrow">
            <span />
            {t("nav.docs")}
          </span>
          <h1>{t("docs.title")}</h1>
          <p>{t("docs.subtitle")}</p>
        </div>
        <div className="docs-layout">
          <aside>
            <h3>{t("docs.general")}</h3>
            {general.map((item) => (
              <a href={`#${item}`} key={item}>
                {t(`docs.${item}Title`)}
              </a>
            ))}
            <h3>{t("docs.demo")}</h3>
            <select
              value={pageId}
              onChange={(event) => setPageId(event.target.value)}
            >
              {catalog.map((page) => (
                <option key={page.id} value={page.id}>
                  {templateTitle(page, i18n.language, t)}
                </option>
              ))}
            </select>
          </aside>
          <article className="docs-content">
            <section className="demo-guide">
              <span className="guide-number">01</span>
              <div>
                <h2>
                  {detail
                    ? templateTitle(detail.page, i18n.language, t)
                    : t("docs.selectDemo")}
                </h2>
                {detail && (
                  <>
                    <p>{templateDescription(detail.page, i18n.language, t)}</p>
                    <div className="guide-stats">
                      <span>
                        {t("docs.editableCount", {
                          count: detail.editableFields,
                        })}
                      </span>
                      <span>
                        {t("catalog.pageCount", {
                          count: detail.family.pages.length,
                        })}
                      </span>
                    </div>
                    <h3>{t("docs.detected")}</h3>
                    <div className="chip-list">
                      {detail.sections.map((item) => (
                        <span key={item.id}>{item.label}</span>
                      ))}
                    </div>
                    <h3>{t("docs.recommendations")}</h3>
                    <div className="chip-list accent">
                      {(detail.recommendations || []).map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                    <div className="guide-callout">
                      <strong>
                        {t(
                          detail.sourceType === "blogger"
                            ? "docs.bloggerNoticeTitle"
                            : "docs.htmlNoticeTitle",
                        )}
                      </strong>
                      <p>
                        {t(
                          detail.sourceType === "blogger"
                            ? "docs.bloggerNoticeText"
                            : "docs.htmlNoticeText",
                        )}
                      </p>
                    </div>
                    <h3>{t("docs.demoWorkflowTitle")}</h3>
                    <ol className="guide-checklist">
                      {Array.from({ length: 8 }, (_, index) => (
                        <li key={index}>
                          <span>{String(index + 1).padStart(2, "0")}</span>
                          <div>
                            <strong>
                              {t(`docs.demoStep${index + 1}Title`)}
                            </strong>
                            <p>{t(`docs.demoStep${index + 1}Text`)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    <h3>{t("docs.editableInventory")}</h3>
                    <div className="inventory-grid">
                      <GuideInventory
                        title={t("docs.headingInventory")}
                        items={detail.headings}
                        empty={t("docs.noneDetected")}
                      />
                      <GuideInventory
                        title={t("docs.imageInventory")}
                        items={detail.images}
                        empty={t("docs.noneDetected")}
                      />
                      <GuideInventory
                        title={t("docs.linkInventory")}
                        items={detail.links}
                        empty={t("docs.noneDetected")}
                      />
                    </div>
                    <h3>{t("docs.fontOptions")}</h3>
                    <div className="chip-list">
                      {detail.supportedFonts.map((font) => (
                        <span key={font}>{font}</span>
                      ))}
                    </div>
                    <h3>{t("docs.localOutput")}</h3>
                    <ul className="dependency-list">
                      {detail.dependencies.map((item) => (
                        <li key={item}>
                          <code>{item}</code>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </section>
            {general.map((item, index) => (
              <section id={item} className="guide-section" key={item}>
                <span className="guide-number">
                  {String(index + 2).padStart(2, "0")}
                </span>
                <div>
                  <h2>{t(`docs.${item}Title`)}</h2>
                  <p>{t(`docs.${item}Text`)}</p>
                  <ol className="general-steps">
                    {[1, 2, 3].map((step) => (
                      <li key={step}>{t(`docs.${item}Step${step}`)}</li>
                    ))}
                  </ol>
                </div>
              </section>
            ))}
          </article>
        </div>
      </main>
    </Shell>
  );
}

function GuideInventory({ title, items = [], empty }) {
  return (
    <div className="inventory-card">
      <strong>{title}</strong>
      {items.length ? (
        <ul>
          {items.slice(0, 12).map((item) => (
            <li key={item.id}>
              <code>{item.tag || item.type}</code>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p>{empty}</p>
      )}
    </div>
  );
}

function Admin() {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [settings, setSettings] = useState(null);
  const [storePricing, setStorePricing] = useState(null);
  const [orders, setOrders] = useState([]);
  const [adminStatus, setAdminStatus] = useState("");
  const load = () =>
    Promise.all([
      api("/admin/users"),
      api("/admin/plans"),
      api("/admin/integrations"),
      api("/admin/store-settings"),
      api("/admin/payment-orders"),
    ]).then(([u, p, i, s, o]) => {
      setUsers(u.users);
      setPlans(p.plans);
      setIntegrations(i);
      setSettings(s.settings);
      setStorePricing(s.pricing);
      setOrders(o.orders);
    });
  useEffect(() => {
    load();
  }, []);
  const updateUser = async (user, changes) => {
    await api(`/admin/users/${user.id}`, {
      method: "PATCH",
      body: {
        planId: changes.planId || user.plan_id,
        status: changes.status || user.status,
      },
    });
    load();
  };
  const savePricing = async (event) => {
    event.preventDefault();
    setAdminStatus("saving");
    try {
      const value = await api("/admin/store-settings", {
        method: "PUT",
        body: {
          basePriceVnd: settings.base_price_vnd,
          promoPriceVnd:
            settings.promo_price_vnd === "" || settings.promo_price_vnd == null
              ? null
              : settings.promo_price_vnd,
          promoStartsAt: settings.promo_starts_at || null,
          promoEndsAt: settings.promo_ends_at || null,
        },
      });
      setSettings(value.settings);
      setStorePricing(value.pricing);
      setAdminStatus("saved");
    } catch (issue) {
      setAdminStatus(issue.message);
    }
  };
  const reviewOrder = async (order, status) => {
    setAdminStatus("reviewing");
    try {
      await api(`/admin/payment-orders/${order.id}`, {
        method: "PATCH",
        body: { status },
      });
      await load();
      setAdminStatus("saved");
    } catch (issue) {
      setAdminStatus(issue.message);
    }
  };
  return (
    <Shell>
      <main className="admin-page">
        <div className="docs-hero">
          <span className="eyebrow">
            <span />
            {t("nav.admin")}
          </span>
          <h1>{t("admin.title")}</h1>
          <p>{t("admin.subtitle")}</p>
        </div>
        {settings && (
          <section className="admin-section commerce-admin">
            <div className="admin-section-heading">
              <div>
                <span>{t("admin.commerceEyebrow")}</span>
                <h2>{t("admin.pricingTitle")}</h2>
                <p>{t("admin.pricingText")}</p>
              </div>
              <div className="admin-price-preview">
                <span>{t("admin.currentPrice")}</span>
                <strong>
                  {formatVnd(
                    storePricing?.currentPriceVnd || settings.base_price_vnd,
                    i18n.language,
                  )}
                </strong>
              </div>
            </div>
            <form className="pricing-form" onSubmit={savePricing}>
              <label>
                <span>{t("admin.basePrice")}</span>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={settings.base_price_vnd}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      base_price_vnd: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                <span>{t("admin.promoPrice")}</span>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={settings.promo_price_vnd ?? ""}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      promo_price_vnd: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                <span>{t("admin.promoStart")}</span>
                <input
                  type="datetime-local"
                  value={localDateTime(settings.promo_starts_at)}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      promo_starts_at: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                <span>{t("admin.promoEnd")}</span>
                <input
                  type="datetime-local"
                  value={localDateTime(settings.promo_ends_at)}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      promo_ends_at: event.target.value,
                    })
                  }
                />
              </label>
              <button className="button primary" type="submit">
                {adminStatus === "saving"
                  ? t("common.saving")
                  : t("admin.savePricing")}
              </button>
            </form>
            <div className="bank-summary">
              <Icon name="shield" size={20} />
              <div>
                <span>{t("admin.paymentAccount")}</span>
                <strong>{settings.bank_account_number}</strong>
                <p>
                  {settings.bank_account_name} · {settings.bank_name} ·{" "}
                  {settings.bank_branch}
                </p>
              </div>
            </div>
          </section>
        )}
        <section className="admin-section">
          <div className="admin-section-heading">
            <div>
              <h2>{t("admin.paymentOrders")}</h2>
              <p>{t("admin.paymentOrdersText")}</p>
            </div>
            <span className="pending-count">
              {orders.filter((order) => order.status === "pending").length}{" "}
              {t("admin.pending")}
            </span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("admin.customer")}</th>
                  <th>{t("admin.product")}</th>
                  <th>{t("admin.amount")}</th>
                  <th>{t("admin.reference")}</th>
                  <th>{t("admin.status")}</th>
                  <th>{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <strong>{order.userName}</strong>
                      <span>{order.userEmail}</span>
                    </td>
                    <td>
                      <strong>{order.projectName}</strong>
                      <span>
                        {t("commerce.revision", { revision: order.revision })}
                      </span>
                    </td>
                    <td>
                      <strong>
                        {formatVnd(order.amountVnd, i18n.language)}
                      </strong>
                    </td>
                    <td>
                      <code>{order.transferReference}</code>
                    </td>
                    <td>
                      <span className={`order-pill ${order.status}`}>
                        {t(`commerce.status.${order.status}`)}
                      </span>
                    </td>
                    <td>
                      {order.status === "pending" && (
                        <div className="review-actions">
                          <button
                            onClick={() => reviewOrder(order, "approved")}
                          >
                            {t("admin.approve")}
                          </button>
                          <button
                            onClick={() => reviewOrder(order, "rejected")}
                          >
                            {t("admin.reject")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!orders.length && (
            <div className="empty-state">{t("admin.noPayments")}</div>
          )}
        </section>
        <section className="admin-section">
          <h2>{t("admin.users")}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("admin.user")}</th>
                  <th>{t("admin.plan")}</th>
                  <th>{t("admin.status")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                      <span>{user.email}</span>
                    </td>
                    <td>
                      <select
                        value={user.plan_id}
                        onChange={(event) =>
                          updateUser(user, { planId: event.target.value })
                        }
                      >
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {t(`plans.${plan.id}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={user.status}
                        onChange={(event) =>
                          updateUser(user, { status: event.target.value })
                        }
                      >
                        <option value="active">{t("admin.active")}</option>
                        <option value="suspended">
                          {t("admin.suspended")}
                        </option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="admin-grid">
          <div className="admin-section">
            <h2>{t("admin.accessPolicy")}</h2>
            <div className="policy-list">
              <div>
                <Icon name="check" />
                <span>
                  <strong>{t("admin.freeEditTitle")}</strong>
                  <small>{t("admin.freeEditText")}</small>
                </span>
              </div>
              <div>
                <Icon name="shield" />
                <span>
                  <strong>{t("admin.paidExportTitle")}</strong>
                  <small>{t("admin.paidExportText")}</small>
                </span>
              </div>
              <div>
                <Icon name="box" />
                <span>
                  <strong>{t("admin.unlimitedTitle")}</strong>
                  <small>{t("admin.unlimitedText")}</small>
                </span>
              </div>
            </div>
          </div>
          <div className="admin-section">
            <h2>{t("admin.integrations")}</h2>
            <div className="integration-row">
              <span className="integration-icon">
                <Icon name="google" size={20} />
              </span>
              <div>
                <strong>{t("admin.google")}</strong>
                <p>
                  {integrations?.google.configured
                    ? t("admin.configured")
                    : t("admin.notConfigured")}
                </p>
              </div>
              <i className={integrations?.google.configured ? "active" : ""} />
            </div>
            <div className="integration-row">
              <span className="integration-icon">
                <Icon name="mail" size={20} />
              </span>
              <div>
                <strong>{t("admin.gmail")}</strong>
                <p>
                  {integrations?.mail.verified
                    ? t("admin.verified")
                    : integrations?.mail.configured
                      ? t("admin.configured")
                      : t("admin.notConfigured")}
                </p>
              </div>
              <i className={integrations?.mail.verified ? "active" : ""} />
            </div>
          </div>
        </section>
      </main>
    </Shell>
  );
}

export default function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/editor/:projectId"
          element={
            <RequireAuth>
              <Editor />
            </RequireAuth>
          }
        />
        <Route path="/docs" element={<Docs />} />
        <Route
          path="/admin"
          element={
            <RequireAuth admin>
              <Admin />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  );
}
